import { NavLink, useNavigate, Link } from "react-router";
import {
  BarChart3,
  Briefcase,
  CheckSquare,
  ChevronsLeft,
  ChevronsRight,
  ClipboardCheck,
  FileDown,
  FileStack,
  FileText,
  Home,
  Inbox,
  Layers,
  ListChecks,
  ListTodo,
  LogOut,
  Mail,
  Phone,
  Receipt,
  Settings,
  Settings2,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/use-permissions";
import { useSidebarCollapsed } from "@/hooks/use-sidebar-collapsed";
import { clearTokens } from "@/lib/auth";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

interface NavItem {
  to: string;
  label: string;
  icon: typeof Home;
  end?: boolean;
  claim?: string;
}

const items: NavItem[] = [
  { to: "/", label: "Dashboard", icon: Home, end: true },
  { to: "/enquiries", label: "Enquiries", icon: Phone, claim: "enquiries:read" },
  { to: "/quotes", label: "Quotes", icon: FileText, claim: "quotes:read" },
  { to: "/jobs", label: "Jobs", icon: Briefcase, claim: "jobs:read" },
  { to: "/jobs/approvals", label: "Approvals", icon: CheckSquare, claim: "jobs:approve" },
  { to: "/tasks", label: "Tasks", icon: ListTodo, claim: "tasks:read" },
  { to: "/invoices", label: "Invoices", icon: Receipt },
  { to: "/accounts", label: "Accounts", icon: Wallet, claim: "accounts:read" },
  { to: "/datasheets", label: "Datasheets", icon: Layers, claim: "datasheet:read" },
  { to: "/statements/runs", label: "Statement runs", icon: FileStack, claim: "statements:read" },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/email/inbox", label: "Inbox", icon: Inbox, claim: "email-inbox:read" },
  { to: "/email/sent", label: "Sent Email", icon: Mail, claim: "email:read-all" },
  { to: "/reports/eod-totals", label: "EOD Totals", icon: BarChart3, claim: "eod:read" },
  { to: "/reports/eod-audit",  label: "EOD Audit",  icon: ListChecks, claim: "audit:read-eod" },
  { to: "/reports/sales-by-month", label: "Sales by Month", icon: TrendingUp, claim: "reporting:read" },
  { to: "/reports/myob-export", label: "MYOB Export", icon: FileDown, claim: "myob-export:read" },
  { to: "/reports/myob-export/config", label: "MYOB Export Config", icon: Settings2, claim: "myob-export:configure" },
  { to: "/reports/daily-recon", label: "Daily Recon", icon: ClipboardCheck, claim: "reconciliation:read-history" },
  { to: "/admin", label: "Admin", icon: Settings, claim: "system-admin:read" },
];

export function AppSidebar() {
  const { has } = usePermissions();
  const visible = items.filter((item) => !item.claim || has(item.claim));
  const { collapsed, toggle } = useSidebarCollapsed();
  const navigate = useNavigate();

  return (
    <TooltipProvider delayDuration={200}>
      <aside
        aria-label="Main navigation"
        className={cn(
          "flex h-full shrink-0 flex-col bg-sidebar text-sidebar-text transition-[width] duration-150 ease-in-out",
          collapsed ? "w-14" : "w-56",
        )}
      >
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2 px-3 py-4 text-sidebar-icon-active">
          <img src="/allmark-logo.png" alt="" aria-hidden="true" className="h-6 w-6" />
          <span
            className={cn(
              "text-sm font-semibold tracking-wide",
              collapsed ? "sr-only" : "",
            )}
          >
            JOB MANAGER
          </span>
        </Link>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-2">
          <ul className="flex flex-col gap-1">
            {visible.map((item) => {
              const Icon = item.icon;
              const link = (
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm",
                      collapsed ? "justify-center" : "justify-start",
                      isActive
                        ? "bg-sidebar-active text-sidebar-icon-active"
                        : "text-sidebar-text hover:bg-sidebar-active/60 hover:text-sidebar-icon-active",
                    )
                  }
                >
                  <Icon className="size-5 shrink-0" aria-hidden="true" />
                  {!collapsed && <span>{item.label}</span>}
                  {collapsed && <span className="sr-only">{item.label}</span>}
                </NavLink>
              );
              return (
                <li key={item.to}>
                  {collapsed ? (
                    <Tooltip>
                      <TooltipTrigger className="w-full">{link}</TooltipTrigger>
                      <TooltipContent side="right">{item.label}</TooltipContent>
                    </Tooltip>
                  ) : (
                    link
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer: user menu + toggle */}
        <div className="flex flex-col gap-2 border-t border-white/10 px-2 py-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  "flex items-center gap-3 rounded-md px-2 py-1 text-sm text-sidebar-text hover:bg-sidebar-active/60 hover:text-sidebar-icon-active",
                  collapsed ? "justify-center" : "justify-start",
                )}
                aria-label="User menu"
              >
                <Avatar className="size-7">
                  <AvatarFallback className="bg-sidebar-active text-sidebar-icon-active text-xs">
                    AA
                  </AvatarFallback>
                </Avatar>
                {!collapsed && <span>Me</span>}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="end">
              <DropdownMenuItem onSelect={() => { void navigate("/me"); }}>
                <Settings className="mr-2 size-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  clearTokens();
                  void navigate("/login", { replace: true });
                }}
              >
                <LogOut className="mr-2 size-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            type="button"
            onClick={toggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn(
              "flex items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-text hover:bg-sidebar-active/60 hover:text-sidebar-icon-active",
              collapsed ? "justify-center" : "justify-start",
            )}
          >
            {collapsed ? (
              <ChevronsRight className="size-4" />
            ) : (
              <ChevronsLeft className="size-4" />
            )}
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>
    </TooltipProvider>
  );
}
